import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import type { Board, Hit, OfflinePlayer, ThemeData, CellMode, CellShape, SquareWithNumbers, TeamMapping } from '../types';
import { DEFAULT_THEME_DATA, getNumberSetKeys, getNumberSetKeyForQuarter } from '../types';
import { getMergedGridDimensions, GRID_CONSTANTS } from './grid';
import type { PlayerOption } from './grid/types';
import { StandardGrid } from './grid/StandardGrid';
import { MergedCellGrid } from './grid/MergedCellGrid';
import { sportsApi } from '../api';

interface SquaresGridProps {
  board: Board;
  grid: SquareWithNumbers[][];
  hits?: Hit[];
  offlinePlayers?: OfflinePlayer[];
  onSquareClick?: (row: number, col: number) => void;
  onAssignSquare?: (row: number, col: number, userId?: string, offlinePlayerId?: string) => Promise<void>;
  onClearSquare?: (row: number, col: number) => Promise<void>;
  currentUserId?: string;
  isManager?: boolean;
  approvedSquares?: number;
  userClaimedCount?: number;
  highlightedCells?: { row: number; col: number }[] | null;
  assignModeEnabled?: boolean;
  assignModePlayerId?: string;
  assignModeAtLimit?: boolean;
  themeData?: ThemeData;
  activeQuarter?: number | null;
  hideNames?: boolean;
}

export const SquaresGrid: React.FC<SquaresGridProps> = ({
  board,
  grid,
  hits = [],
  offlinePlayers = [],
  onSquareClick,
  onAssignSquare,
  onClearSquare,
  currentUserId,
  isManager = false,
  approvedSquares,
  userClaimedCount = 0,
  highlightedCells,
  assignModeEnabled = false,
  assignModePlayerId,
  assignModeAtLimit = false,
  themeData: providedThemeData,
  activeQuarter,
  hideNames = false,
}) => {
  // Theme
  const rawThemeData = providedThemeData || board.theme?.themeData;
  const themeData = rawThemeData ? { ...DEFAULT_THEME_DATA, ...rawThemeData } : DEFAULT_THEME_DATA;

  // Team mappings for live game boards (logos)
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
  useEffect(() => {
    if (!board.externalGameId) return;
    let mounted = true;
    sportsApi.getGame(board.externalGameId).then(data => {
      if (mounted) setTeamMappings(data.teamMappings || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [board.externalGameId]);

  // Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState<{ row: number; col: number } | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerOption | null>(null);

  // Hover state (shared across grid modes)
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const handleCellMouseEnter = useCallback((row: number, col: number) => {
    setHoveredCell({ row, col });
  }, []);
  const handleCellMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  // Hit counts
  const hitCounts = useMemo(() => {
    const counts = new Map<string, number>();
    hits.forEach((h) => {
      const key = `${h.winningRow}-${h.winningCol}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [hits]);

  const getHitCount = (row: number, col: number): number => {
    return hitCounts.get(`${row}-${col}`) || 0;
  };

  // Bad number detection
  const isBadNumberSquare = useCallback((row: number, col: number): boolean => {
    const config = board.payoutConfig?.badNumbersConfig;
    if (!config?.enabled || !board.numbersRevealed || !board.homeNumbers || !board.awayNumbers) {
      return false;
    }
    const rowNum = board.awayNumbers[row];
    const colNum = board.homeNumbers[col];
    const combo = `${rowNum}-${colNum}`;
    if (config.badCombinations && config.badCombinations.length > 0) {
      return config.badCombinations.includes(combo);
    }
    const badDigits = config.badDigits || [];
    if (badDigits.length === 0) return false;
    return badDigits.includes(rowNum) && badDigits.includes(colNum);
  }, [board.payoutConfig?.badNumbersConfig, board.numbersRevealed, board.homeNumbers, board.awayNumbers]);

  // Grid dimensions
  const { cellSize, headerSize } = GRID_CONSTANTS;
  const cellMode = (board.cellMode || 'standard') as CellMode;
  const cellShape = (board.cellShape || '1x1') as CellShape;
  const gridDimensions = useMemo(
    () => getMergedGridDimensions(cellMode, cellShape),
    [cellMode, cellShape]
  );
  const { rows: visualRows, rowSpan } = gridDimensions;
  const mergedCellHeight = cellSize * rowSpan + (rowSpan - 1) * GRID_CONSTANTS.gap;
  const gridHeight = mergedCellHeight * visualRows + GRID_CONSTANTS.gap * (visualRows - 1);

  // Team/number resolution
  const homeOnTop = board.homeOnTop ?? true;
  const topTeam = homeOnTop ? board.homeTeam : board.awayTeam;
  const leftTeam = homeOnTop ? board.awayTeam : board.homeTeam;
  const topNumbers = homeOnTop ? board.homeNumbers : board.awayNumbers;
  const leftNumbers = homeOnTop ? board.awayNumbers : board.homeNumbers;
  const topTeamType = homeOnTop ? 'Home' : 'Away';
  const leftTeamType = homeOnTop ? 'Away' : 'Home';
  // Resolve team mappings for live game boards
  const homeTeamMapping = teamMappings.find(t => t.teamName === board.homeTeam || t.abbreviation === board.homeTeam);
  const awayTeamMapping = teamMappings.find(t => t.teamName === board.awayTeam || t.abbreviation === board.awayTeam);

  // For live boards with classic/no theme, use actual team colors from mappings
  const isClassicOrNoTheme = !board.themeId || board.theme?.lookupKey === 'classic';
  const liveHomeColor = isClassicOrNoTheme && homeTeamMapping?.primaryColor ? homeTeamMapping.primaryColor : undefined;
  const liveAwayColor = isClassicOrNoTheme && awayTeamMapping?.primaryColor ? awayTeamMapping.primaryColor : undefined;

  const homeColor = liveHomeColor || (board.themeId ? themeData.homeTeamColor : (board.homeColor || themeData.homeTeamColor));
  const awayColor = liveAwayColor || (board.themeId ? themeData.awayTeamColor : (board.awayColor || themeData.awayTeamColor));
  const homeAccent = themeData.homeTeamAccent;
  const awayAccent = themeData.awayTeamAccent;
  const topColor = homeOnTop ? homeColor : awayColor;
  const leftColor = homeOnTop ? awayColor : homeColor;
  const topAccent = homeOnTop ? homeAccent : awayAccent;
  const leftAccent = homeOnTop ? awayAccent : homeAccent;
  const topTeamLogoUrl = homeOnTop ? homeTeamMapping?.logoUrl : awayTeamMapping?.logoUrl;
  const leftTeamLogoUrl = homeOnTop ? awayTeamMapping?.logoUrl : homeTeamMapping?.logoUrl;

  // Multi-number mode
  const numberMode = board.numberMode || 'single';
  const numberSetKeys = getNumberSetKeys(board.quarters, numberMode);
  const hasMultipleNumberSets = numberSetKeys.length > 0;

  const getTopNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
    }
    return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
  };

  const getLeftNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
    }
    return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
  };

  const isActiveNumberSetKey = (key: string): boolean => {
    if (activeQuarter === undefined || activeQuarter === null) return true;
    const activeKey = getNumberSetKeyForQuarter(activeQuarter, board.quarters, numberMode);
    return activeKey === key;
  };

  // Player list for assignment dialog
  const getBoardPlayers = (): PlayerOption[] => {
    const players: PlayerOption[] = [];
    const addedUserIds = new Set<string>();
    board.managers?.forEach((m) => {
      if (m.user?.displayName && !addedUserIds.has(m.userId)) {
        addedUserIds.add(m.userId);
        players.push({ id: m.userId, displayName: m.user.displayName, isOfflinePlayer: false });
      }
    });
    board.participants?.forEach((p) => {
      if (p.user?.displayName && !addedUserIds.has(p.userId)) {
        addedUserIds.add(p.userId);
        players.push({ id: p.userId, displayName: p.user.displayName, isOfflinePlayer: false });
      }
    });
    offlinePlayers.forEach((op) => {
      players.push({ id: op.id, displayName: `${op.displayName} (offline)`, isOfflinePlayer: true });
    });
    return players.sort((a, b) => {
      if (a.isOfflinePlayer !== b.isOfflinePlayer) {
        return a.isOfflinePlayer ? 1 : -1;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  };

  const boardPlayers = getBoardPlayers();

  // Click handler that routes to assign mode, draft, or live behavior
  const handleSquareClick = (row: number, col: number) => {
    if (board.status === 'locked') return;

    const square = grid[row]?.[col];

    if (assignModeEnabled && onSquareClick) {
      const isSelectedPlayersSquare = assignModePlayerId && (
        square?.ownerId === assignModePlayerId || square?.offlinePlayerId === assignModePlayerId
      );
      const isEmptySquare = !square?.ownerId && !square?.offlinePlayerId;

      if (isEmptySquare && !assignModeAtLimit) {
        onSquareClick(row, col);
      } else if (isSelectedPlayersSquare && onClearSquare) {
        onClearSquare(row, col);
      }
      return;
    }

    if (board.status === 'draft') {
      if (isManager && onAssignSquare) {
        setSelectedSquare({ row, col });
        const players = getBoardPlayers();
        if (square?.ownerId) {
          setSelectedPlayer(players.find(p => p.id === square.ownerId && !p.isOfflinePlayer) || null);
        } else if (square?.offlinePlayerId) {
          setSelectedPlayer(players.find(p => p.id === square.offlinePlayerId && p.isOfflinePlayer) || null);
        } else {
          setSelectedPlayer(null);
        }
        setAssignDialogOpen(true);
      }
      return;
    }

    if (isManager && onAssignSquare) {
      setSelectedSquare({ row, col });
      const players = getBoardPlayers();
      if (square?.ownerId) {
        setSelectedPlayer(players.find(p => p.id === square.ownerId && !p.isOfflinePlayer) || null);
      } else if (square?.offlinePlayerId) {
        setSelectedPlayer(players.find(p => p.id === square.offlinePlayerId && p.isOfflinePlayer) || null);
      } else {
        setSelectedPlayer(null);
      }
      setAssignDialogOpen(true);
    } else if (onSquareClick) {
      if (square?.ownerId === currentUserId && onClearSquare) {
        onClearSquare(row, col);
      } else if (!square?.ownerId && !square?.offlinePlayerId) {
        if (approvedSquares !== undefined && userClaimedCount >= approvedSquares) {
          return;
        }
        onSquareClick(row, col);
      }
    }
  };

  const handleAssign = async () => {
    if (selectedSquare && selectedPlayer && onAssignSquare) {
      if (selectedPlayer.isOfflinePlayer) {
        await onAssignSquare(selectedSquare.row, selectedSquare.col, undefined, selectedPlayer.id);
      } else {
        await onAssignSquare(selectedSquare.row, selectedSquare.col, selectedPlayer.id, undefined);
      }
      setAssignDialogOpen(false);
      setSelectedSquare(null);
      setSelectedPlayer(null);
    }
  };

  const handleClaimForSelf = async () => {
    if (selectedSquare && onSquareClick) {
      onSquareClick(selectedSquare.row, selectedSquare.col);
      setAssignDialogOpen(false);
      setSelectedSquare(null);
      setSelectedPlayer(null);
    }
  };

  const handleClear = async () => {
    if (selectedSquare && onClearSquare) {
      await onClearSquare(selectedSquare.row, selectedSquare.col);
      setAssignDialogOpen(false);
      setSelectedSquare(null);
      setSelectedPlayer(null);
    }
  };

  const isSquareClaimed = selectedSquare
    ? !!(grid[selectedSquare.row]?.[selectedSquare.col]?.ownerId || grid[selectedSquare.row]?.[selectedSquare.col]?.offlinePlayerId)
    : false;

  // Shared props for the grid sub-component
  const gridProps = {
    board,
    grid,
    themeData,
    cellMode,
    cellShape,
    topNumbers,
    leftNumbers,
    topColor,
    leftColor,
    topAccent,
    leftAccent,
    numberSetKeys,
    hasMultipleNumberSets,
    getTopNumbersForKey,
    getLeftNumbersForKey,
    isActiveNumberSetKey,
    getHitCount,
    isBadNumberSquare,
    currentUserId,
    isManager,
    approvedSquares,
    userClaimedCount,
    highlightedCells,
    assignModeEnabled,
    assignModePlayerId,
    assignModeAtLimit,
    hideNames,
    onSquareClick: handleSquareClick,
    onClearSquare,
    hoveredCell,
    onCellMouseEnter: handleCellMouseEnter,
    onCellMouseLeave: handleCellMouseLeave,
    leftTeam,
    leftTeamType,
    gridHeight,
    topTeamLogoUrl,
    leftTeamLogoUrl,
  };

  const GridComponent = cellMode === 'standard' ? StandardGrid : MergedCellGrid;

  return (
    <Box sx={{ overflowX: 'auto', pr: 2 }}>
      <Box sx={{ display: 'inline-block', minWidth: 'fit-content', pr: 1 }}>
        {/* Top Team Name */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Box sx={{
            width: hasMultipleNumberSets
              ? headerSize + (numberSetKeys.length * (headerSize * 0.8 + 2))
              : headerSize + headerSize
          }} />
          <Box
            sx={{
              width: cellSize * 10 + GRID_CONSTANTS.gap * 9 + GRID_CONSTANTS.gap + 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {topTeamLogoUrl && (
              <Box
                component="img"
                src={topTeamLogoUrl}
                alt={topTeam}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                sx={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: topColor,
                fontSize: '1.1rem',
              }}
            >
              {topTeam} ({topTeamType})
            </Typography>
            {topTeamLogoUrl && (
              <Box sx={{ width: 28, flexShrink: 0 }} />
            )}
          </Box>
        </Box>

        {/* Delegated Grid Rendering (top numbers + main flex row with left team, left numbers, grid) */}
        <GridComponent {...gridProps} />

        {/* Legend */}
        {(() => {
          const legendTextColor = themeData.textColorMode === 'light' ? '#ffffff' : undefined;
          return (
            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: themeData.squareUnclaimed, border: `1px solid ${themeData.cellBorder}` }} />
                <Typography variant="caption" sx={{ color: legendTextColor }}>Available</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: themeData.squareClaimedSelf }} />
                <Typography variant="caption" sx={{ color: legendTextColor }}>Your Squares</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: themeData.squareClaimedOther }} />
                <Typography variant="caption" sx={{ color: legendTextColor }}>Claimed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: themeData.squareWinner }} />
                <Typography variant="caption" sx={{ color: legendTextColor }}>Winner</Typography>
              </Box>
              {assignModeEnabled && assignModePlayerId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: themeData.squareSelected }} />
                  <Typography variant="caption" sx={{ color: legendTextColor }}>Selected Player</Typography>
                </Box>
              )}
              {board.payoutConfig?.badNumbersConfig?.enabled && board.numbersRevealed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: '#4caf50',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                  }}>
                    ✓
                  </Box>
                  <Typography variant="caption" sx={{ color: legendTextColor }}>Bad Number (Auto Win)</Typography>
                </Box>
              )}
              {board.payoutConfig?.boxNeighborPayouts?.enabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    border: `3px solid ${themeData.boxNeighborBorderColor || '#1976d2'}`,
                    borderRadius: 0.5,
                    backgroundColor: 'transparent',
                  }} />
                  <Typography variant="caption" sx={{ color: legendTextColor }}>
                    Box Neighbor ({board.payoutConfig.boxNeighborPayouts.boxSize})
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>

      {/* Assign Square Dialog (for managers) */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isSquareClaimed ? 'Reassign or Clear Square' : 'Assign Square'} ({selectedSquare?.row}, {selectedSquare?.col})
        </DialogTitle>
        <DialogContent>
          {isSquareClaimed && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Currently assigned to: {grid[selectedSquare?.row ?? 0]?.[selectedSquare?.col ?? 0]?.owner?.displayName || grid[selectedSquare?.row ?? 0]?.[selectedSquare?.col ?? 0]?.offlinePlayer?.displayName || 'Unknown'}
            </Typography>
          )}
          <Autocomplete
            sx={{ mt: 1 }}
            options={boardPlayers}
            value={selectedPlayer}
            onChange={(_, newValue) => setSelectedPlayer(newValue)}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign to Player"
                placeholder="Search players..."
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.displayName}
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          {isSquareClaimed && onClearSquare && (
            <Button onClick={handleClear} color="error" variant="outlined">
              Clear / Unassign
            </Button>
          )}
          {currentUserId && !isSquareClaimed && (
            <Button onClick={handleClaimForSelf} variant="outlined">
              Claim for Myself
            </Button>
          )}
          <Button onClick={handleAssign} variant="contained" disabled={!selectedPlayer}>
            {isSquareClaimed ? 'Reassign' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
